import React from 'react';
import { ControlledInputField } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-input-field';
import { ControlledTextArea } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-textarea';
import { ControlledFileUpload } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-file-upload';
import {
    MAX_ADDITIONAL_INFORMATION_LENGTH,
    MAX_ADDRESS_LENGTH,
    MAX_NAME_LENGTH,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/advance-request-property/consts';
import { FormControl } from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import { SupportEmailInput } from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import { FileWithUrl, FlexContainer } from '@placer-ui/components';
import { getFeatureFlags } from 'core/flow-control';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/offline-reports/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';

import styles from './site-plan-request-property.module.scss';

const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'image/png', // PNG (Portable Network Graphics)
    'image/jpeg',
    'image/jpg',
    'text/plain',
    'application/zip',
    'application/x-zip',
    'application/x-zip-compressed',
    '.kmz',
    '.kml',
];

export interface SitePlanFormValues {
    type: 'advance_property_site_plan';
    poiName: string;
    address: string;
    additionalInformation: string;
    files?: FileWithUrl[];
    executiveEmail?: string;
    opportunityUrl?: string;
}

export const SitePlanRequestProperty = ({
    formControl,
}: {
    formControl: FormControl<SitePlanFormValues>;
}) => {
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();
    const { control } = formControl;
    return (
        <div className={styles.formContainer} data-testid="advance-site-plan-request">
            {enable_request_properties_sales_flow_ff && (
                <FlexContainer gap={16}>
                    <SupportSalesEmailInput />
                    <SupportOpportunityUrlInput />
                </FlexContainer>
            )}
            {!enable_request_properties_sales_flow_ff && <SupportEmailInput />}
            <FlexContainer gap="16px">
                <div className={styles.inputBox}>
                    <ControlledInputField
                        name="poiName"
                        label="Property Name"
                        placeholder="Name"
                        data-testid="site-plan-name-input"
                        control={control}
                        asterix
                        required
                        maxLength={MAX_NAME_LENGTH}
                    />
                </div>
                <div className={styles.inputBox}>
                    <ControlledInputField
                        name="address"
                        label="Address"
                        placeholder="Address"
                        data-testid="site-plan-address-input"
                        control={control}
                        asterix
                        required
                        maxLength={MAX_ADDRESS_LENGTH}
                    />
                </div>
            </FlexContainer>
            <ControlledTextArea
                name="additionalInformation"
                control={control}
                initialHeight={100}
                label="Additional Information"
                data-testid="site-plan-additional-information-input"
                feedback={[
                    {
                        message: `Up to ${MAX_ADDITIONAL_INFORMATION_LENGTH} characters`,
                        state: 'guidance',
                        hideIcon: true,
                    },
                ]}
                maxLength={MAX_ADDITIONAL_INFORMATION_LENGTH}
                placeholder="Share additional details to help our support team assist you better (optional)."
            />
            <ControlledFileUpload
                name="files"
                formats={SUPPORTED_FILE_TYPES}
                label="Attachments"
                feedback={[]}
                description="PDF"
                control={control}
                required
                asterix
            />
        </div>
    );
};
